import {
  createStylesParam,
  createStylesParams,
  StyleParamType,
  wixColorParam,
  wixFontParam,
} from '@wix/tpa-settings';
import type { CustomCssVarsFn } from '@wix/yoshi-flow-editor';

import {
  BadgeLayout,
  BadgeSize,
  ProfileAlignment,
  ProfileImage,
  ProfileImageSize,
  ProfileLayout,
  ProfileWidgetHeight,
} from '../../types';
import {
  getBadgesCustomCssVars,
  getProfileAlignmentCustomCssVars,
  getProfileImageCustomCssVars,
} from './customCssVars';

export type IStylesParams = {
  showCover: StyleParamType.Boolean;
  showMessageButton: StyleParamType.Boolean;
  showMessageButtonMobile: StyleParamType.Boolean;
  profileLayout: StyleParamType.Number;
  profileImageLayout: StyleParamType.Number;
  profileImageSize: StyleParamType.Number;
  profileAlignment: StyleParamType.Number;
  badgeLayout: StyleParamType.Number;
  badgeSize: StyleParamType.Number;
  badgeCornerRadius: StyleParamType.Number;
  badgeFont: StyleParamType.Font;
  textPrimaryColor: StyleParamType.Color;
  textPrimaryFont: StyleParamType.Font;
  textSecondaryColor: StyleParamType.Color;
  textSecondaryFont: StyleParamType.Font;
  memberNameResponsiveColor: StyleParamType.Color;
  memberNameResponsiveFont: StyleParamType.Font;
  memberNameMobileColor: StyleParamType.Color;
  memberNameMobileFont: StyleParamType.Font;
  titleFont: StyleParamType.Font;
  titleColor: StyleParamType.Color;
  titleResponsiveFont: StyleParamType.Font;
  titleResponsiveColor: StyleParamType.Color;
  titleMobileFont: StyleParamType.Font;
  titleMobileColor: StyleParamType.Color;
  ffTextResponsiveColor: StyleParamType.Color;
  ffTextResponsiveFont: StyleParamType.Font;
  ffTextMobileColor: StyleParamType.Color;
  ffTextMobileFont: StyleParamType.Font;
  buttonColor: StyleParamType.Color;
  buttonResponsiveColor: StyleParamType.Color;
  buttonMobileColor: StyleParamType.Color;
  buttonFont: StyleParamType.Font;
  buttonResponsiveFont: StyleParamType.Font;
  buttonMobileFont: StyleParamType.Font;
  boxColor: StyleParamType.Color;
  boxBorderWidth: StyleParamType.Number;
  boxBorderColor: StyleParamType.Color;
  coverColor: StyleParamType.Color;
  coverMobileColor: StyleParamType.Color;
  coverPhotoOpacity: StyleParamType.Number;
  coverPhotoOpacityDesktop: StyleParamType.Number;
  profileWidgetHeight: StyleParamType.Number;
  buttonFontColor: StyleParamType.Color;
  buttonResponsiveFontColor: StyleParamType.Color;
  buttonBorderColor: StyleParamType.Color;
  buttonResponsiveBorderColor: StyleParamType.Color;
  buttonBorderWidth: StyleParamType.Number;
  buttonResponsiveBorderWidth: StyleParamType.Number;
  buttonTextFontSizeMobile: StyleParamType.Number;
  coverColorDesktop: StyleParamType.Color;
  buttonCornerRadius: StyleParamType.Number;
  buttonResponsiveCornerRadius: StyleParamType.Number;
  verticalWidgetCornerRadius: StyleParamType.Number;
};

export const defaultButtonColor = 'color-8';

export const defaultProfileLayout = ProfileLayout.Card;

export const defaultProfileImageLayout = ProfileImage.Round;

export const defaultProfileAlignment = ProfileAlignment.Left;

export const defaultProfileImageSize = ProfileImageSize.Medium;

export const defaultBadgeLayout = BadgeLayout.NameAndIcon;

export const defaultBadgeSize = BadgeSize.Small;

export const defaultProfileWidgetHeight = ProfileWidgetHeight.Small;

export const defaultBadgeFontTextPreset = 'Body-M';

const defaultBadgeCornerRadius = 10;

const coverColor = createStylesParam('pw-cover-color', {
  type: StyleParamType.Color,
  getDefaultValue: wixColorParam('color-8'),
});

const coverColorDesktop = createStylesParam('pw-cover-color-desktop', {
  type: StyleParamType.Color,
  dangerousKeyTransformationOverride: () => 'pw-cover-color-desktop',
  getDefaultValue: ({ getStyleParamValue }) => {
    return getStyleParamValue(coverColor) ?? wixColorParam('color-8');
  },
});

const coverMobileColor = createStylesParam('pw-cover-color-mobile', {
  type: StyleParamType.Color,
  dangerousKeyTransformationOverride: () => 'pw-cover-color-mobile',
  getDefaultValue: ({ getStyleParamValue }) => {
    return getStyleParamValue(coverColorDesktop) ?? wixColorParam('color-8');
  },
});

const coverPhotoOpacity = createStylesParam('pw-cover-photo-opacity', {
  type: StyleParamType.Number,
  getDefaultValue: () => 60,
});

const coverPhotoOpacityDesktop = createStylesParam(
  'pw-cover-photo-opacity-desktop',
  {
    type: StyleParamType.Number,
    dangerousKeyTransformationOverride: () => 'pw-cover-photo-opacity-desktop',
    getDefaultValue: ({ getStyleParamValue }) => {
      const coverPhotoOpacityValue = getStyleParamValue(coverPhotoOpacity);
      return coverPhotoOpacityValue === undefined ? 60 : coverPhotoOpacityValue;
    },
  },
);

const buttonColor = createStylesParam('button-opacity-and-color', {
  type: StyleParamType.Color,
  getDefaultValue: wixColorParam(defaultButtonColor),
});

const buttonBorderColor = createStylesParam('pw-button-border-color', {
  type: StyleParamType.Color,
  getDefaultValue: ({ getStyleParamValue }) =>
    getStyleParamValue(buttonColor) ?? wixColorParam(defaultButtonColor),
});

const buttonResponsiveColor = createStylesParam('pw-responsive-button-color', {
  type: StyleParamType.Color,
  getDefaultValue: wixColorParam('color-1'),
});

const buttonResponsiveBorderColor = createStylesParam(
  'pw-button-responsive-border-color',
  {
    type: StyleParamType.Color,
    getDefaultValue: ({ getStyleParamValue }) =>
      getStyleParamValue(buttonResponsiveColor) ??
      wixColorParam(defaultButtonColor),
  },
);

const buttonMobileFont = createStylesParam('pw-button-font-mobile', {
  type: StyleParamType.Font,
  dangerousKeyTransformationOverride: () => 'pw-button-font-mobile',
  getDefaultValue: wixFontParam('Body-M', { size: 16 }),
});

const buttonTextFontSizeMobile = createStylesParam(
  'pw-button-text-font-size-mobile',
  {
    type: StyleParamType.Number,
    dangerousKeyTransformationOverride: () => 'pw-button-text-font-size-mobile',
    getDefaultValue: ({ getStyleParamValue }) => {
      return getStyleParamValue(buttonMobileFont as any)?.size || 16;
    },
  },
);

export default createStylesParams<IStylesParams>({
  showCover: {
    type: StyleParamType.Boolean,
    key: 'showCover',
    getDefaultValue: () => false,
  },
  showMessageButton: {
    type: StyleParamType.Boolean,
    key: 'showMessageButton',
    dangerousKeyTransformationOverride: () => 'showMessageButton',
    getDefaultValue: () => true,
  },
  showMessageButtonMobile: {
    type: StyleParamType.Boolean,
    key: 'showMessageButtonMobile',
    dangerousKeyTransformationOverride: () => 'showMessageButtonMobile',
    getDefaultValue: () => true,
  },
  profileLayout: {
    type: StyleParamType.Number,
    key: 'profileLayout',
    getDefaultValue: () => defaultProfileLayout,
  },
  profileImageLayout: {
    type: StyleParamType.Number,
    key: 'pictureStyle',
    getDefaultValue: () => defaultProfileImageLayout,
  },
  profileAlignment: {
    type: StyleParamType.Number,
    key: 'profileAlignment',
    getDefaultValue: () => defaultProfileAlignment,
  },
  profileImageSize: {
    type: StyleParamType.Number,
    key: 'profileImageSize',
    getDefaultValue: () => defaultProfileImageSize,
  },
  badgeLayout: {
    type: StyleParamType.Number,
    key: 'badge-layout',
    getDefaultValue: () => defaultBadgeLayout,
  },
  badgeSize: {
    type: StyleParamType.Number,
    key: 'badge-size',
    getDefaultValue: () => defaultBadgeSize,
  },
  badgeCornerRadius: {
    type: StyleParamType.Number,
    key: 'badge-corner-radius',
    getDefaultValue: () => defaultBadgeCornerRadius,
  },
  badgeFont: {
    type: StyleParamType.Font,
    key: 'badge-font',
    getDefaultValue: wixFontParam(defaultBadgeFontTextPreset),
  },
  textPrimaryColor: {
    type: StyleParamType.Color,
    key: 'text-color-primary',
    getDefaultValue: wixColorParam('color-5'),
  },
  textPrimaryFont: {
    type: StyleParamType.Font,
    key: 'text-primary-font',
    getDefaultValue: wixFontParam('Body-M', { size: 20 }),
  },
  textSecondaryColor: {
    type: StyleParamType.Color,
    key: 'text-color-secondary',
    getDefaultValue: wixColorParam('color-5'),
  },
  textSecondaryFont: {
    type: StyleParamType.Font,
    key: 'text-secondary-font',
    getDefaultValue: wixFontParam('Body-M', { size: 14 }),
  },
  memberNameResponsiveColor: {
    type: StyleParamType.Color,
    key: 'pw-responsive-name-color',
    getDefaultValue: wixColorParam('color-1'),
  },
  memberNameResponsiveFont: {
    type: StyleParamType.Font,
    key: 'pw-responsive-name-font',
    getDefaultValue: wixFontParam('Body-M', { size: 28 }),
  },
  memberNameMobileColor: {
    type: StyleParamType.Color,
    key: 'pw-name-color-mobile',
    dangerousKeyTransformationOverride: () => 'pw-name-color-mobile',
    getDefaultValue: wixColorParam('color-5'),
  },
  memberNameMobileFont: {
    type: StyleParamType.Font,
    key: 'pw-name-font-mobile',
    dangerousKeyTransformationOverride: () => 'pw-name-font-mobile',
    getDefaultValue: wixFontParam('Body-M', { size: 20 }),
  },
  titleFont: {
    type: StyleParamType.Font,
    key: 'title-font',
    getDefaultValue: wixFontParam('Body-M', { size: 14 }),
  },
  titleColor: {
    type: StyleParamType.Color,
    key: 'title-color',
    getDefaultValue: wixColorParam('color-5'),
  },
  titleResponsiveFont: {
    type: StyleParamType.Font,
    key: 'title-responsive-font',
    getDefaultValue: wixFontParam('Body-M', { size: 20 }),
  },
  titleResponsiveColor: {
    type: StyleParamType.Color,
    key: 'title-responsive-color',
    getDefaultValue: wixColorParam('color-1'),
  },
  titleMobileFont: {
    type: StyleParamType.Font,
    key: 'title-mobile-font',
    dangerousKeyTransformationOverride: () => 'title-mobile-font',
    getDefaultValue: wixFontParam('Body-M', { size: 16 }),
  },
  titleMobileColor: {
    type: StyleParamType.Color,
    key: 'title-mobile-color',
    dangerousKeyTransformationOverride: () => 'title-mobile-color',
    getDefaultValue: wixColorParam('color-5'),
  },
  ffTextResponsiveColor: {
    type: StyleParamType.Color,
    key: 'pw-responsive-ff-color',
    getDefaultValue: wixColorParam('color-1'),
  },
  ffTextResponsiveFont: {
    type: StyleParamType.Font,
    key: 'pw-responsive-ff-font',
    getDefaultValue: wixFontParam('Body-M', { size: 16 }),
  },
  ffTextMobileColor: {
    type: StyleParamType.Color,
    key: 'pw-ff-color-mobile',
    dangerousKeyTransformationOverride: () => 'pw-ff-color-mobile',
    getDefaultValue: wixColorParam('color-5'),
  },
  ffTextMobileFont: {
    type: StyleParamType.Font,
    key: 'pw-ff-font-mobile',
    dangerousKeyTransformationOverride: () => 'pw-ff-font-mobile',
    getDefaultValue: wixFontParam('Body-M', { size: 12 }),
  },
  buttonColor,
  buttonResponsiveColor,
  buttonMobileColor: {
    type: StyleParamType.Color,
    key: 'pw-responsive-button-color-mobile',
    dangerousKeyTransformationOverride: () => {
      return 'pw-responsive-button-color-mobile';
    },
    getDefaultValue: wixColorParam('color-1'),
  },
  buttonFont: {
    type: StyleParamType.Font,
    key: 'button-font',
    getDefaultValue: wixFontParam('Body-M', { size: 16 }),
  },
  buttonResponsiveFont: {
    type: StyleParamType.Font,
    key: 'pw-responsive-button-font',
    getDefaultValue: wixFontParam('Body-M', { size: 16 }),
  },
  buttonMobileFont,
  boxColor: {
    type: StyleParamType.Color,
    key: 'box-color',
    dangerousKeyTransformationOverride: () => 'box-color',
    getDefaultValue: wixColorParam('color-1'),
  },
  boxBorderWidth: {
    type: StyleParamType.Number,
    key: 'border-width-1',
    dangerousKeyTransformationOverride: () => 'border-width-1',
    getDefaultValue: () => 1,
  },
  boxBorderColor: {
    type: StyleParamType.Color,
    key: 'border-color',
    dangerousKeyTransformationOverride: () => 'border-color',
    getDefaultValue: wixColorParam('color-5', 0.2),
  },
  coverColor,
  coverColorDesktop,
  coverMobileColor,
  coverPhotoOpacity,
  coverPhotoOpacityDesktop,
  profileWidgetHeight: {
    type: StyleParamType.Number,
    key: 'profileWidgetHeight',
    getDefaultValue: () => defaultProfileWidgetHeight,
  },
  buttonFontColor: {
    type: StyleParamType.Color,
    key: 'pw-button-font-color',
    getDefaultValue: wixColorParam('color-1'),
  },
  buttonResponsiveFontColor: {
    type: StyleParamType.Color,
    key: 'pw-button-responsive-font-color',
    getDefaultValue: wixColorParam('color-8'),
  },
  buttonBorderColor,
  buttonResponsiveBorderColor,
  buttonBorderWidth: {
    type: StyleParamType.Number,
    key: 'pw-button-border-width',
    getDefaultValue: () => 1,
  },
  buttonResponsiveBorderWidth: {
    type: StyleParamType.Number,
    key: 'pw-button-responsive-border-width',
    getDefaultValue: () => 1,
  },
  buttonTextFontSizeMobile,
  buttonCornerRadius: {
    key: 'pw-button-corner-radius',
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
  },
  buttonResponsiveCornerRadius: {
    key: 'pw-button-responsive-corner-radius',
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
  },
  verticalWidgetCornerRadius: {
    key: 'vertical-pw-corner-radius',
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
  },
});

export const customCssVars: CustomCssVarsFn = (props) => {
  const { styleParams } = props;
  const { numbers } = styleParams;

  const profileImageCustomCssVars = getProfileImageCustomCssVars(props);
  const badgesCustomCssVars = getBadgesCustomCssVars(props);
  const profileAlignmentCustomCssVars = getProfileAlignmentCustomCssVars(props);

  return {
    profileWidgetHeight: `${numbers.profileWidgetHeight}px`,
    ...profileImageCustomCssVars,
    ...profileAlignmentCustomCssVars,
    ...badgesCustomCssVars,
  };
};
